import { useRouter } from "next/router";
import { useState, useEffect } from "react";
import {
  Flex,
  Text,
  Center,
  Drawer,
  DrawerBody,
  DrawerOverlay,
  DrawerContent,
} from "@chakra-ui/react";
import { useSelector } from "react-redux";
import Pagination from "@material-ui/lab/Pagination";

import { wrapper } from "../../../src/redux/store";
import CategoryDrawer from "../../../src/components/categoryDrawer";
import ProductCard from "../../../src/components/products/productCard";
import {
  Error,
  TopBand,
  FreeDeliveryOffer,
} from "../../../src/components/common";

import MenuIcon from "../../../src/assets/svg/menu";

import {
  fetchProducts,
  fetchProductCategories,
} from "../../../src/redux/actions";
import { capitalizeFirstLetter } from "../../../src/utils/functions";
import { fetchAllProductCategories } from "../../../src/services/productCategories";

import { ITEMS_PER_PAGE_ODD } from "../../../src/constants";

export default function CategoryProducts({ errorMessage, filteredProducts }) {
  if (errorMessage) {
    return <Error errorMessage={errorMessage} />;
  }

  const router = useRouter();
  const {
    query: { category },
  } = router;

  const [page, setPage] = useState(1);
  const [filterDrawer, setFilterDrawer] = useState(false);

  const { cart, productCategories } = useSelector((state) => state);
  const cartProductIds = cart.map((product) => product.id);

  const openFilterDrawer = () => setFilterDrawer(true);
  const closeFilterDrawer = () => setFilterDrawer(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [page]);

  return (
    <>
      <TopBand title={`Our Products for ${capitalizeFirstLetter(category)}`} />
      <Center mt="10" flexDirection="column">
        <Flex
          mb="5"
          alignItems="center"
          width={{ base: "90%", "2xl": "1350px" }}
        >
          <Flex
            alignItems={{ base: "flex-start", sm: "center" }}
            justifyContent={{ base: "flex-start", sm: "center" }}
            flexDirection={{ base: "column", sm: "row" }}
          >
            <Flex alignItems={{ base: "center" }}>
              <MenuIcon
                color="maroon"
                height="20"
                width="20"
                onClick={openFilterDrawer}
              />
              <Text ml="2" color="maroon" variant="headerText">
                Filter
              </Text>
              <Text ml="5" color="titleColor" variant="productCardDescription">
                {`Showing ${(page - 1) * ITEMS_PER_PAGE_ODD + 1}-${Math.min(
                  page * ITEMS_PER_PAGE_ODD,
                  filteredProducts.length
                )} of ${filteredProducts.length} Results`}
              </Text>
            </Flex>
            <FreeDeliveryOffer />
          </Flex>
        </Flex>

        <Flex
          flexWrap="wrap"
          width={{ xl: "90%", "2xl": "1350px" }}
          justifyContent={{
            base: "center",
            md: "space-around",
            lg: "space-around",
            xl: "space-between",
            "2xl": "space-between",
          }}
        >
          {filteredProducts
            .slice((page - 1) * ITEMS_PER_PAGE_ODD, page * ITEMS_PER_PAGE_ODD)
            .map((product) => (
              <ProductCard
                key={product?.id}
                product={product}
                cartProductIds={cartProductIds}
              />
            ))}
        </Flex>
      </Center>
      <Center my="10">
        <Pagination
          page={page}
          size="large"
          color="primary"
          variant="outlined"
          onChange={(event, val) => setPage(val)}
          count={
            filteredProducts
              ? Math.ceil(filteredProducts.length / ITEMS_PER_PAGE_ODD)
              : 0
          }
        />
      </Center>
      <Drawer
        size="xs"
        placement="left"
        isOpen={filterDrawer}
        onClose={closeFilterDrawer}
      >
        <DrawerOverlay>
          <DrawerContent>
            <DrawerBody>
              <Text variant="drawerTitleText" my="5">
                Product Categories
              </Text>
              <CategoryDrawer
                closeFilterDrawer={closeFilterDrawer}
                productCategories={productCategories}
              />
            </DrawerBody>
          </DrawerContent>
        </DrawerOverlay>
      </Drawer>
    </>
  );
}

export const getStaticProps = wrapper.getStaticProps(
  (store) =>
    async ({ params }) => {
      try {
        const products = await store.dispatch(fetchProducts());
        await store.dispatch(fetchProductCategories());

        const filteredProducts = [];
        products.forEach((product) => {
          product.categories.forEach((cat) => {
            if (cat.slug === params.category) {
              filteredProducts.push(product);
            }
          });
        });
        return {
          props: { filteredProducts },
        };
      } catch (err) {
        return {
          props: {
            filteredProducts: [],
            errorMessage: err.message,
          },
        };
      }
    }
);

export async function getStaticPaths() {
  try {
    const response = await fetchAllProductCategories();
    if (!response) {
      throw { message: "getStaticPaths: Pages not found at {url}" };
    }

    const primaryCategories = response.filter((cat) => !cat.parent);
    const paths = primaryCategories.map(({ slug }) => ({
      params: { category: slug },
    }));

    return {
      paths,
      fallback: false,
    };
  } catch (err) {
    console.log(err);
    return { paths: [], fallback: false };
  }
}
